import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'redux/store';
import { Button, Spinner, Container, Form, ListGroup } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { fetchWorkflows } from '../../../../redux/slices/workflowsSlice';
import { RootState } from '../../../../redux/store';
import { selectHour12Option } from 'redux/selectors/userSettings';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Split from 'react-split';

import WorkflowDetailsPanel from './WorkflowDetailPanel';

const WORKFLOWS_PAGE_SIZE = 25;
type WorkflowFilter = 'all' | 'open' | 'closed';

const WorkflowDetailedView = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { workflowId } = useParams<{ workflowId: string }>();
  const { workflows, filterOptions, pagination, loading } = useSelector((state: RootState) => state.workflows);
  const hour12 = useSelector(selectHour12Option);

  const paramFilter = searchParams.get('filter');
  const initialFilter: WorkflowFilter = paramFilter === 'open' || paramFilter === 'closed' ? paramFilter : 'all';

  const [filter, setFilter] = useState<WorkflowFilter>(initialFilter);

  const [workflowSort, setWorkflowSort] = useState<'latest' | 'oldest'>('latest');

  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);

  const parsedPage = parseInt(searchParams.get('page') || '1', 10);
  const page = isNaN(parsedPage) ? 1 : Math.max(1, parsedPage);

  const resolvedWorkflowId = workflowId ? Number(workflowId) : Math.max(...workflows.map((wf) => wf.workflow_id), 0);

  useEffect(() => {
    dispatch(
      fetchWorkflows({
        filter,
        page,
        limit: WORKFLOWS_PAGE_SIZE,
        sortOrder: workflowSort === 'latest' ? 'desc' : 'asc',
      })
    );
  }, [dispatch, filter, page, workflowSort]);

  const hasOpenAction = (workflow) => {
    return workflow.workflows_actions.some((action) => action.response_options.length > 0 && action.response === null);
  };

  const handleResize = () => setIsSidebarVisible(window.innerWidth >= 768);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const order = searchParams.get('sortOrder');
    if (order === 'asc') setWorkflowSort('oldest');
    else if (order === 'desc') setWorkflowSort('latest');
    else {
      const defaultOrder = workflowSort === 'latest' ? 'desc' : 'asc';
      searchParams.set('sortOrder', defaultOrder);
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggleSort = () => {
    const nextSort = workflowSort === 'latest' ? 'oldest' : 'latest';
    setWorkflowSort(nextSort);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('sortOrder', nextSort === 'latest' ? 'desc' : 'asc');
    nextParams.set('page', '1');
    setSearchParams(nextParams, { replace: true });
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newFilter = e.target.value as WorkflowFilter;
    setFilter(newFilter);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('filter', newFilter);
    nextParams.set('page', '1');
    setSearchParams(nextParams, { replace: true });
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('page', String(nextPage));
    setSearchParams(nextParams, { replace: true });
  };

  const handleSidebarScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    e.currentTarget.style.boxShadow = scrollTop < scrollHeight - clientHeight ? 'inset 0px -8px 8px -8px rgba(0, 0, 0, 0.3)' : 'none';

    const nearBottom = scrollTop + clientHeight >= scrollHeight - 120;
    const currentPage = pagination?.current_page || page;
    const totalPages = pagination?.total_pages || 0;
    if (nearBottom && !loading && totalPages > 0 && currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  useEffect(() => {
    if (!searchParams.get('filter') || !searchParams.get('page')) {
      const nextParams = new URLSearchParams(searchParams);
      if (!nextParams.get('filter')) nextParams.set('filter', 'all');
      if (!nextParams.get('page')) nextParams.set('page', '1');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (loading && workflows.length === 0) return <Spinner animation="border" />;

  const search = searchParams.toString();
  const withSearch = search ? `?${search}` : '';
  return (
    <Container fluid className="p-0 d-flex flex-column flex-grow-1" style={{ minHeight: 0 }}>
      <Split className="d-flex flex-grow-1" style={{ minHeight: 0 }} sizes={[20, 80]} minSize={150} expandToMin>
        {isSidebarVisible && workflows && (
          <div
            style={{
              overflowY: 'auto',
              height: '100%',
              minHeight: 0,
              borderRight: '1px solid #dee2e6',
              padding: '0',
              boxShadow: 'inset 0px -8px 8px -8px rgba(0, 0, 0, 0.3)',
            }}
            onScroll={handleSidebarScroll}
          >
            <h3>INBOX</h3>

            <div className="d-flex justify-content-between align-items-center px-2 pt-2">
              <span className="text-muted fw-semibold">{t('Sort by:')}</span>
              <Button variant="link" size="sm" className="p-0 text-muted" onClick={toggleSort}>
                {workflowSort === 'latest' ? 'latest' : 'oldest'}
              </Button>
            </div>

            <div className="px-2 pb-2">
              <Form.Select size="sm" value={filter} onChange={handleFilterChange}>
                {filterOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {t(opt)}
                  </option>
                ))}
              </Form.Select>
            </div>

            <ListGroup variant="flush">
              {[...workflows]
                .sort((a, b) =>
                  workflowSort === 'latest'
                    ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    : new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                )
                .map((workflow) => (
                  <ListGroup.Item
                    key={workflow.workflow_id}
                    as={Link}
                    to={`/inbox/${workflow.workflow_id}${withSearch}`}
                    active={workflow.workflow_id === resolvedWorkflowId}
                    className={`d-flex justify-content-between align-items-center ${hasOpenAction(workflow) ? 'fw-bold' : ''}`}
                  >
                    <div>{workflow.workflow_subject}</div>
                    <small className="text-muted">
                      {new Date(workflow.created_at).toLocaleString('nl-NL', {
                        day: 'numeric',
                        month: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12,
                      })}
                    </small>
                  </ListGroup.Item>
                ))}
            </ListGroup>
            {loading && (
              <div className="d-flex justify-content-center py-2 border-top">
                <Spinner animation="border" size="sm" />
              </div>
            )}
          </div>
        )}

        {resolvedWorkflowId ? (
          <WorkflowDetailsPanel workflow_id={resolvedWorkflowId} markAsRead />
        ) : (
          <div
            style={{
              padding: '0.5rem',
              overflow: 'auto',
              height: '100%',
              minHeight: 0,
              flexGrow: 1,
            }}
          >
            <p className="m-0">{t('workflows.no_selected_workflow')}</p>
          </div>
        )}
      </Split>
    </Container>
  );
};

export default WorkflowDetailedView;
